export async function fetchSchemaWithCache(store, options = {}) {
  const {
    objectType,
    projectId = null,
    service = null,
    maxAttempts = 50,
    pollInterval = 100,
  } = options;

  if (!objectType) {
    return {
      success: false,
      error: "Object type is required",
    };
  }

  try {
    let existingSchema = store.getters["schema/getSchemaBySlug"](
      objectType,
      projectId,
      service,
    );
    let isLoading = store.getters["schema/isSchemaLoading"](
      objectType,
      projectId,
      service,
    );

    let response;

    if (existingSchema) {
      response = { success: true, schema: existingSchema };
    } else if (isLoading) {
      let attempts = 0;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, pollInterval));

        existingSchema = store.getters["schema/getSchemaBySlug"](
          objectType,
          projectId,
          service,
        );
        isLoading = store.getters["schema/isSchemaLoading"](
          objectType,
          projectId,
          service,
        );

        if (existingSchema) {
          response = { success: true, schema: existingSchema };
          break;
        }

        if (!isLoading) {
          response = await store.dispatch("schema/fetchSchema", {
            slug: objectType,
            projectId: projectId,
            service,
          });
          break;
        }

        attempts++;
      }

      if (!response) {
        return {
          success: false,
          error: "Timeout waiting for schema to load",
        };
      }
    } else {
      response = await store.dispatch("schema/fetchSchema", {
        slug: objectType,
        projectId: projectId,
        service,
      });
    }

    return response;
  } catch (error) {
    console.error("[schemaUtils] Error fetching schema:", error);
    return {
      success: false,
      error: error.message || "An error occurred while fetching schema",
    };
  }
}

export function getSchemaKey(objectType, projectId = null) {
  return objectType === "tasks" && projectId
    ? `tasks_${projectId}`
    : objectType;
}

export function mapObjectIdsToRelatedObjects(objectIds, availableObjects) {
  if (!objectIds || !Array.isArray(objectIds)) {
    return [];
  }

  return objectIds.map((objectId) => {
    const obj = availableObjects.find((o) => o.value === objectId);
    const base = {
      object_id: objectId,
      object_type: obj?.slug || objectId,
    };
    if (obj?.service) base.object_service = obj.service;
    return base;
  });
}

export function getSlugById(attributes, fieldId) {
  if (!attributes || !Array.isArray(attributes) || !fieldId) {
    return fieldId;
  }
  const attr = attributes.find((a) => a.id === fieldId);
  return attr?.slug || fieldId;
}

export function getIdBySlug(attributes, slug) {
  if (!attributes || !Array.isArray(attributes) || !slug) {
    return slug;
  }
  const attr = attributes.find((a) => a.slug === slug);
  return attr?.id || slug;
}

export function findAttributeById(attributes, fieldId) {
  if (!attributes || !Array.isArray(attributes) || !fieldId) {
    return null;
  }
  return attributes.find((a) => a.id === fieldId) || null;
}

export function findAttributeBySlug(attributes, slug) {
  if (!attributes || !Array.isArray(attributes) || !slug) {
    return null;
  }
  return attributes.find((a) => a.slug === slug) || null;
}

export function getSourceOptionsFromAttribute(sourceAttr) {
  const rawOptions = sourceAttr?.structured_options?.length > 0
    ? sourceAttr.structured_options.map((opt) => opt.label || opt.name)
    : sourceAttr?.options?.length > 0
      ? sourceAttr.options.map((opt) => typeof opt === "string" ? opt : (opt?.label || opt?.name || opt?.value))
      : [];

  return rawOptions
    .filter((opt) => opt && String(opt).trim() !== "")
    .map((opt) => String(opt));
}

export function shouldExcludeById(excludedIds, property) {
  if (!excludedIds || !Array.isArray(excludedIds) || !property) {
    return false;
  }
  const fieldId = property.id;
  if (!fieldId) return false;
  return excludedIds.some(
    (excludedId) => excludedId.toLowerCase() === fieldId.toLowerCase(),
  );
}
